/**
 * Keep the screen awake while a consult is being recorded (CLA-1341).
 *
 * On an iPhone the browser's microphone is suspended the moment the screen
 * locks, and the auto-lock is typically 30 seconds. A clinician who puts the
 * phone face-down to talk to the patient loses the rest of the consult without
 * a single visible symptom — the timer keeps counting. Holding a screen wake
 * lock removes the commonest trigger. It cannot stop the user pressing the
 * side button or switching apps; the mute detection in the store covers those.
 *
 * The lock is released by the OS whenever the page is hidden, so it is
 * re-requested on every return to visibility until `release` is called.
 * Best-effort throughout: unsupported browsers, a denied request (low-power
 * mode) and a hidden page all degrade to "no lock", never to an error.
 */
export function holdWakeLock(doc: Document = document, nav: Navigator = navigator): () => void {
  const wakeLock = nav.wakeLock
  if (!wakeLock || typeof doc.addEventListener !== 'function') return () => {}

  let sentinel: WakeLockSentinel | null = null
  let released = false

  const acquire = async () => {
    if (released || sentinel || doc.visibilityState !== 'visible') return
    try {
      const s = await wakeLock.request('screen')
      if (released) {
        void s.release()
        return
      }
      sentinel = s
      s.addEventListener('release', () => {
        if (sentinel === s) sentinel = null
      })
    } catch {
      // Denied — low-power mode, or a policy. Nothing to do; recording carries on.
    }
  }

  const onVisibility = () => void acquire()
  doc.addEventListener('visibilitychange', onVisibility)
  void acquire()

  return () => {
    released = true
    doc.removeEventListener('visibilitychange', onVisibility)
    const s = sentinel
    sentinel = null
    if (s) void s.release().catch(() => {})
  }
}
